import React, { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Save, Loader2, Eye, EyeOff, Plus, Trash2, GripVertical, Languages, AlertTriangle } from "lucide-react";
import IconSelector, { getIconByName } from "@/components/admin/IconSelector";
import RichTextEditor from "@/components/admin/RichTextEditor";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface PageContent {
  id: string;
  page_slug: string;
  section_key: string;
  content_en: string;
  content_fr: string;
  is_hidden: boolean;
  display_order: number;
}

// Define section groups for Home page in display order
const HOME_SECTION_GROUPS = [
  {
    title: "Hero Section",
    sections: ["hero_title", "hero_subtitle", "hero_view_talent", "hero_our_services"],
  },
  {
    title: "Trust Highlights",
    sections: [
      "trust_title1", "trust_desc1",
      "trust_title2", "trust_desc2",
      "trust_title3", "trust_desc3",
    ],
  },
  {
    title: "Featured Talent",
    sections: ["featured_title", "featured_subtitle", "featured_view_all"],
  },
  {
    title: "Services Preview",
    sections: [
      "services_title", "services_subtitle",
      "services_item1_title", "services_item1_desc",
      "services_item2_title", "services_item2_desc",
      "services_item3_title", "services_item3_desc",
    ],
  },
  {
    title: "How It Works",
    sections: [
      "howitworks_title", "howitworks_subtitle",
      "howitworks_step1_title", "howitworks_step1_desc",
      "howitworks_step2_title", "howitworks_step2_desc",
      "howitworks_step3_title", "howitworks_step3_desc",
    ],
  },
  {
    title: "Call to Action",
    sections: ["home_cta_title", "home_cta_subtitle", "home_cta_button"],
  },
];

// Define section groups for About page in display order
const ABOUT_SECTION_GROUPS = [
  {
    title: "Page Header",
    sections: ["hero_title", "hero_subtitle", "intro"],
  },
  {
    title: "History",
    sections: ["history_title", "history_text"],
  },
  {
    title: "What We Do",
    sections: [
      "whatwedo_title",
      "whatwedo_talent_title",
      "whatwedo_talent_desc",
      "whatwedo_casting_title",
      "whatwedo_casting_desc",
      "whatwedo_session_title",
      "whatwedo_session_desc",
    ],
  },
  {
    title: "Our Approach",
    sections: ["approach_title", "approach_text"],
  },
  {
    title: "Agency Director",
    sections: ["director_title", "director_role", "director_bio"],
  },
  {
    title: "Why Producers Choose Us",
    sections: [
      "whyproducers_title",
      "whyproducers_fast_title",
      "whyproducers_fast_desc",
      "whyproducers_bilingual_title",
      "whyproducers_bilingual_desc",
      "whyproducers_pricing_title",
      "whyproducers_pricing_desc",
      "whyproducers_local_title",
      "whyproducers_local_desc",
    ],
  },
];

// Define section groups for For Talent page in display order
const FOR_TALENT_SECTION_GROUPS = [
  {
    title: "Page Header",
    sections: ["hero_title", "hero_subtitle", "intro"],
  },
  {
    title: "Criteria",
    sections: ["criteria_title", "criteria_subtitle", "criteria_items"],
  },
  {
    title: "Benefits",
    sections: [
      "benefits_title",
      "benefits_subtitle",
      "benefit1_title",
      "benefit1_desc",
      "benefit2_title",
      "benefit2_desc",
      "benefit3_title",
      "benefit3_desc",
      "benefit4_title",
      "benefit4_desc",
      "benefit5_title",
      "benefit5_desc",
      "benefit6_title",
      "benefit6_desc",
    ],
  },
  {
    title: "Application Form",
    sections: ["form_title", "form_subtitle"],
  },
];

// Service category prefixes - used to dynamically build groups
const SERVICE_CATEGORY_PREFIXES = [
  "voiceover",
  "oncamera", 
  "production",
  "photography",
  "video",
  "language",
];

// Static section groups for Services page (header, CTA, and contact info)
const SERVICES_STATIC_GROUPS = {
  header: {
    title: "Page Header",
    sections: ["services_title", "services_subtitle"],
  },
  cta: {
    title: "Call to Action",
    sections: ["cta_title", "cta_intro"],
  },
  hours: {
    title: "Hours of Operation",
    sections: ["hours_title", "hours_weekend"],
    days: [
      { day: "Monday", open: "hours_monday_open", close: "hours_monday_close" },
      { day: "Tuesday", open: "hours_tuesday_open", close: "hours_tuesday_close" },
      { day: "Wednesday", open: "hours_wednesday_open", close: "hours_wednesday_close" },
      { day: "Thursday", open: "hours_thursday_open", close: "hours_thursday_close" },
      { day: "Friday", open: "hours_friday_open", close: "hours_friday_close" },
    ],
  },
  contact: {
    title: "Contact Information",
    sections: ["contact_email", "contact_phone", "contact_note"],
  },
};

const SECTION_LABELS: Record<string, string> = {
  // Home page - Hero
  hero_title: "Title",
  hero_subtitle: "Subtitle",
  hero_view_talent: "View Talent Button",
  hero_our_services: "Our Services Button",
  // Home page - Trust Highlights
  trust_title1: "Highlight 1 Title",
  trust_desc1: "Highlight 1 Description",
  trust_title2: "Highlight 2 Title",
  trust_desc2: "Highlight 2 Description",
  trust_title3: "Highlight 3 Title",
  trust_desc3: "Highlight 3 Description",
  // Home page - Featured Talent
  featured_title: "Title",
  featured_subtitle: "Subtitle",
  featured_view_all: "View All Button",
  // Home page - Services Preview
  services_item1_title: "Service 1 Title",
  services_item1_desc: "Service 1 Description",
  services_item2_title: "Service 2 Title",
  services_item2_desc: "Service 2 Description",
  services_item3_title: "Service 3 Title",
  services_item3_desc: "Service 3 Description",
  // Home page - How It Works
  howitworks_title: "Title",
  howitworks_subtitle: "Subtitle",
  howitworks_step1_title: "Step 1 Title",
  howitworks_step1_desc: "Step 1 Description",
  howitworks_step2_title: "Step 2 Title",
  howitworks_step2_desc: "Step 2 Description",
  howitworks_step3_title: "Step 3 Title",
  howitworks_step3_desc: "Step 3 Description",
  // Home page - CTA
  home_cta_title: "Title",
  home_cta_subtitle: "Subtitle",
  home_cta_button: "Button Text",
  // About page - Hero
  about_hero_title: "Title",
  about_hero_subtitle: "Subtitle",
  intro: "Intro Text",
  // About page - History
  history_title: "Title",
  history_text: "Content",
  // About page - What We Do
  whatwedo_title: "Title",
  whatwedo_talent_title: "Subtitle 1",
  whatwedo_talent_desc: "Description 1",
  whatwedo_casting_title: "Subtitle 2",
  whatwedo_casting_desc: "Description 2",
  whatwedo_session_title: "Subtitle 3",
  whatwedo_session_desc: "Description 3",
  // About page - Our Approach
  approach_title: "Title",
  approach_text: "Content",
  // About page - Director
  director_title: "Title",
  director_role: "Role",
  director_bio: "Biography",
  // About page - Why Producers Choose Us
  whyproducers_title: "Title",
  whyproducers_fast_title: "Subtitle 1",
  whyproducers_fast_desc: "Description 1",
  whyproducers_bilingual_title: "Subtitle 2",
  whyproducers_bilingual_desc: "Description 2",
  whyproducers_pricing_title: "Subtitle 3",
  whyproducers_pricing_desc: "Description 3",
  whyproducers_local_title: "Subtitle 4",
  whyproducers_local_desc: "Description 4",
  // Services page
  services_title: "Title",
  services_subtitle: "Subtitle",
  voiceover_title: "Title",
  voiceover_description: "Description",
  voiceover_usecases: "Genres (one per line)",
  oncamera_title: "Title",
  oncamera_description: "Description",
  oncamera_usecases: "Genres (one per line)",
  production_title: "Title",
  production_description: "Description",
  production_usecases: "Genres (one per line)",
  photography_title: "Title",
  photography_description: "Description",
  photography_usecases: "Genres (one per line)",
  video_title: "Title",
  video_description: "Description",
  video_usecases: "Genres (one per line)",
  language_title: "Title",
  language_description: "Description",
  language_usecases: "Genres (one per line)",
  cta_title: "Title",
  cta_intro: "Intro Text",
  cta_description: "Description",
  // Hours of Operation
  hours_title: "Section Title",
  hours_monday: "Monday",
  hours_tuesday: "Tuesday",
  hours_wednesday: "Wednesday",
  hours_thursday: "Thursday",
  hours_friday: "Friday",
  hours_weekend: "Weekend Note",
  // Contact Information
  contact_email: "Email Address",
  contact_phone: "Phone Number",
  contact_note: "Additional Note",
  // For Talent page
  criteria_title: "Title",
  criteria_subtitle: "Subtitle",
  criteria_items: "Criteria Items (one per line)",
  benefits_title: "Title",
  benefits_subtitle: "Subtitle",
  benefit1_title: "Benefit 1 Title",
  benefit1_desc: "Benefit 1 Description",
  benefit2_title: "Benefit 2 Title",
  benefit2_desc: "Benefit 2 Description",
  benefit3_title: "Benefit 3 Title",
  benefit3_desc: "Benefit 3 Description",
  benefit4_title: "Benefit 4 Title",
  benefit4_desc: "Benefit 4 Description",
  benefit5_title: "Benefit 5 Title",
  benefit5_desc: "Benefit 5 Description",
  benefit6_title: "Benefit 6 Title",
  benefit6_desc: "Benefit 6 Description",
  // Application Form
  form_title: "Form Title",
  form_subtitle: "Form Subtitle",
};

// Fields that use the rich text editor
const RICH_TEXT_FIELDS = [
  "intro",
  "history_text",
  "approach_text",
  "director_bio",
  "whatwedo_talent_desc",
  "whatwedo_casting_desc",
  "whatwedo_session_desc",
  "voiceover_description",
  "oncamera_description",
  "production_description",
  "photography_description",
  "video_description",
  "language_description",
  "cta_description",
];

// Fields that use textarea for multi-line input
const TEXTAREA_FIELDS = [
  "voiceover_usecases",
  "oncamera_usecases",
  "production_usecases",
  "photography_usecases",
  "video_usecases",
  "language_usecases",
  "criteria_items",
];

// Sortable Service Card Component
interface SortableServiceCardProps {
  group: { id: string; title: string; sections: string[]; prefix: string };
  contentByKey: Record<string, PageContent>;
  localChanges: Record<string, { content_en: string; content_fr: string }>;
  showFrench: boolean;
  currentField: "content_en" | "content_fr";
  handleChange: (id: string, field: "content_en" | "content_fr", value: string, original: PageContent) => void;
  handleToggleVisibility: (group: { title: string; sections: string[] }) => void;
  handleDeleteCategory: (prefix: string) => void;
  saveGroupChanges: (sectionKeys: string[]) => void;
  hasGroupChanges: (sectionKeys: string[]) => boolean;
  isGroupHidden: (sections: string[]) => boolean;
  toggleVisibilityMutation: { isPending: boolean };
  updateMutation: { isPending: boolean; mutate: (data: { id: string; content_en: string; content_fr: string }) => void };
  onCreateIcon: (prefix: string, iconName: string, displayOrder: number) => Promise<void>;
}

const SortableServiceCard = ({
  group,
  contentByKey,
  localChanges,
  showFrench,
  currentField,
  handleChange,
  handleToggleVisibility,
  handleDeleteCategory,
  saveGroupChanges,
  hasGroupChanges,
  isGroupHidden,
  toggleVisibilityMutation,
  updateMutation,
  onCreateIcon,
}: SortableServiceCardProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: group.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const groupHidden = isGroupHidden(group.sections);

  return (
    <Card 
      ref={setNodeRef} 
      style={style} 
      className={groupHidden ? "opacity-60" : ""}
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing p-1 -ml-1 text-muted-foreground hover:text-foreground"
            >
              <GripVertical className="h-4 w-4" />
            </button>
            <CardTitle className="text-lg">{group.title}</CardTitle>
            {groupHidden && (
              <span className="text-xs bg-muted px-2 py-0.5 rounded text-muted-foreground">Hidden</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive hover:text-destructive"
                  title="Delete category"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Service Category?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete this service category and all its content. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => handleDeleteCategory(group.prefix)}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button
              onClick={() => handleToggleVisibility(group)}
              disabled={toggleVisibilityMutation.isPending}
              size="sm"
              variant="ghost"
              title={groupHidden ? "Show section" : "Hide section"}
            >
              {groupHidden ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </Button>
            <Button
              onClick={() => saveGroupChanges(group.sections)}
              disabled={!hasGroupChanges(group.sections) || updateMutation.isPending}
              size="sm"
              variant={hasGroupChanges(group.sections) ? "default" : "outline"}
            >
              {updateMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Icon and Title row */}
        {(() => {
          const titleItem = contentByKey[group.sections[0]];
          if (!titleItem) return null;
          const titleValues = localChanges[titleItem.id] ?? { content_en: titleItem.content_en, content_fr: titleItem.content_fr };
          
          // Get the icon item
          const iconItem = contentByKey[`${group.prefix}_icon`];
          const iconValue = iconItem?.content_en || "Briefcase";
          
          // Handler to create icon record if missing, then update
          const handleIconChange = async (newIcon: string) => {
            if (iconItem) {
              updateMutation.mutate({
                id: iconItem.id,
                content_en: newIcon,
                content_fr: newIcon,
              });
            } else {
              // Create the icon record if it doesn't exist
              await onCreateIcon(group.prefix, newIcon, titleItem.display_order || 0);
            }
          };
          
          return (
            <div className="grid md:grid-cols-[180px_1fr] gap-4 items-end">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-muted-foreground">
                  Icon
                </label>
                <IconSelector
                  value={iconValue}
                  onChange={handleIconChange}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-muted-foreground">
                  {SECTION_LABELS[titleItem.section_key] || "Title"}
                </label>
                <Input
                  value={showFrench ? titleValues.content_fr : titleValues.content_en}
                  onChange={(e) => handleChange(titleItem.id, currentField, e.target.value, titleItem)}
                  placeholder={`Enter ${showFrench ? "French" : "English"} content...`}
                />
              </div>
            </div>
          );
        })()}
        {/* 2-column grid for description/usecases */}
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-4">
          {[group.sections[1], group.sections[2]].map(sectionKey => {
            const item = contentByKey[sectionKey];
            if (!item) return null;

            const isRichText = RICH_TEXT_FIELDS.includes(item.section_key);
            const isTextarea = TEXTAREA_FIELDS.includes(item.section_key) || item.section_key.endsWith("_usecases");
            const currentValues = localChanges[item.id] ?? { content_en: item.content_en, content_fr: item.content_fr };
            const displayValue = showFrench ? currentValues.content_fr : currentValues.content_en;

            return (
              <div key={item.id} className="space-y-1.5">
                <label className="text-sm font-medium text-muted-foreground">
                  {SECTION_LABELS[item.section_key] || (item.section_key.endsWith("_description") ? "Description" : "Genres (one per line)")}
                </label>
                {isRichText || item.section_key.endsWith("_description") ? (
                  <RichTextEditor
                    value={displayValue}
                    onChange={(value) => handleChange(item.id, currentField, value, item)}
                    placeholder={`Enter ${showFrench ? "French" : "English"} content...`}
                  />
                ) : isTextarea ? (
                  <Textarea
                    value={displayValue}
                    onChange={(e) => handleChange(item.id, currentField, e.target.value, item)}
                    placeholder={`Enter ${showFrench ? "French" : "English"} content (one per line)...`}
                    rows={6}
                  />
                ) : (
                  <Input
                    value={displayValue}
                    onChange={(e) => handleChange(item.id, currentField, e.target.value, item)}
                    placeholder={`Enter ${showFrench ? "French" : "English"} content...`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

const PagesAdmin = () => {
  const [selectedPage, setSelectedPage] = useState("home");
  const [showFrench, setShowFrench] = useState(false);
  const [localChanges, setLocalChanges] = useState<Record<string, { content_en: string; content_fr: string }>>({});
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const hasUnsavedChanges = Object.keys(localChanges).length > 0;

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Hours & Contact tab fetches from "services" page_slug since that's where the data is stored
  const queryPageSlug = selectedPage === "hours-contact" ? "services" : selectedPage;

  const { data: pageContent, isLoading } = useQuery({
    queryKey: ["page-content", selectedPage],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("page_content")
        .select("*")
        .eq("page_slug", queryPageSlug)
        .order("section_key");
      
      if (error) throw error;
      return data as PageContent[];
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, content_en, content_fr }: { id: string; content_en: string; content_fr: string }) => {
      const { error } = await supabase
        .from("page_content")
        .update({ content_en, content_fr })
        .eq("id", id);
      
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["page-content"] });
      setLocalChanges(prev => {
        const { [variables.id]: _, ...rest } = prev;
        return rest;
      });
      toast({
        title: "Saved",
        description: "Content updated successfully.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update content.",
        variant: "destructive",
      });
    },
  });

  const toggleVisibilityMutation = useMutation({
    mutationFn: async ({ id, is_hidden }: { id: string; is_hidden: boolean }) => {
      const { error } = await supabase
        .from("page_content")
        .update({ is_hidden })
        .eq("id", id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["page-content"] });
      toast({
        title: "Updated",
        description: "Section visibility updated.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update visibility.",
        variant: "destructive",
      });
    },
  });

  const addCategoryMutation = useMutation({
    mutationFn: async (prefix: string) => {
      const sections = [
        { page_slug: "services", section_key: `${prefix}_title`, content_en: "New Service", content_fr: "Nouveau service" },
        { page_slug: "services", section_key: `${prefix}_description`, content_en: "", content_fr: "" },
        { page_slug: "services", section_key: `${prefix}_usecases`, content_en: "", content_fr: "" },
        { page_slug: "services", section_key: `${prefix}_icon`, content_en: "Briefcase", content_fr: "Briefcase" },
      ];
      
      const { error } = await supabase
        .from("page_content")
        .insert(sections);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["page-content"] });
      toast({
        title: "Added",
        description: "New service category added.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to add service category.",
        variant: "destructive",
      });
    },
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: async (prefix: string) => {
      const { error } = await supabase
        .from("page_content")
        .delete()
        .eq("page_slug", "services")
        .in("section_key", [`${prefix}_title`, `${prefix}_description`, `${prefix}_usecases`, `${prefix}_icon`]);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["page-content"] });
      toast({
        title: "Deleted",
        description: "Service category deleted.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete service category.",
        variant: "destructive",
      });
    },
  });

  const reorderMutation = useMutation({
    mutationFn: async (updates: { prefix: string; order: number }[]) => {
      // Update display_order for all sections of each category
      for (const update of updates) {
        const { error } = await supabase
          .from("page_content")
          .update({ display_order: update.order })
          .eq("page_slug", "services")
          .in("section_key", [
            `${update.prefix}_title`,
            `${update.prefix}_description`,
            `${update.prefix}_usecases`,
            `${update.prefix}_icon`,
          ]);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["page-content"] });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to reorder categories.",
        variant: "destructive",
      });
    },
  });

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Create a map for quick lookup
  const contentByKey = useMemo(() => {
    return pageContent?.reduce((acc, item) => {
      acc[item.section_key] = item;
      return acc;
    }, {} as Record<string, PageContent>) ?? {};
  }, [pageContent]);

  // Dynamically build service category groups from the data
  const serviceCategories = useMemo(() => {
    if (!pageContent || selectedPage !== "services") return [];
    
    // Find all unique prefixes that have _title, _description, _usecases
    const prefixMap = new Map<string, number>();
    pageContent.forEach(item => {
      const match = item.section_key.match(/^(.+)_(title|description|usecases)$/);
      if (match && match[1] !== "services" && match[1] !== "cta") {
        // Use the display_order from any of the three sections (they should be the same)
        if (!prefixMap.has(match[1])) {
          prefixMap.set(match[1], item.display_order || 0);
        }
      }
    });

    // Filter to only include prefixes that have all three sections, sorted by display_order
    return Array.from(prefixMap.entries())
      .filter(([prefix]) => {
        return contentByKey[`${prefix}_title`] && 
               contentByKey[`${prefix}_description`] && 
               contentByKey[`${prefix}_usecases`];
      })
      .sort((a, b) => a[1] - b[1])
      .map(([prefix]) => prefix);
  }, [pageContent, selectedPage, contentByKey]);

  // Build section groups for current page (excluding CTA for services - rendered separately)
  const sectionGroups = useMemo(() => {
    if (selectedPage === "home") {
      return HOME_SECTION_GROUPS;
    }
    if (selectedPage === "about") {
      return ABOUT_SECTION_GROUPS;
    }
    if (selectedPage === "for-talent") {
      return FOR_TALENT_SECTION_GROUPS;
    }
    if (selectedPage === "hours-contact") {
      // Hours & Contact tab - no regular section groups, handled separately below
      return [];
    }
    
    // Build services groups dynamically (header only, categories handled separately)
    return [SERVICES_STATIC_GROUPS.header];
  }, [selectedPage]);

  // Service category groups for drag-and-drop
  const serviceCategoryGroups = useMemo(() => {
    return serviceCategories.map((prefix, index) => ({
      id: prefix,
      title: `Service Category ${index + 1}`,
      sections: [`${prefix}_title`, `${prefix}_description`, `${prefix}_usecases`],
      prefix,
    }));
  }, [serviceCategories]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = serviceCategories.indexOf(active.id as string);
      const newIndex = serviceCategories.indexOf(over.id as string);
      
      const newOrder = arrayMove(serviceCategories, oldIndex, newIndex);
      
      // Update display_order for all categories
      const updates = newOrder.map((prefix, index) => ({
        prefix,
        order: index,
      }));
      
      reorderMutation.mutate(updates);
    }
  };

  const handleChange = (id: string, field: "content_en" | "content_fr", value: string, original: PageContent) => {
    // Skip if value hasn't actually changed from the original
    const currentOriginal = field === "content_en" ? original.content_en : original.content_fr;
    const prevValue = localChanges[id]?.[field];
    
    // If we're setting back to original value and no other field is changed, remove from localChanges
    if (value === currentOriginal && !localChanges[id]) {
      return; // No change needed
    }
    
    setLocalChanges(prev => {
      const newChanges = {
        ...prev,
        [id]: {
          content_en: field === "content_en" ? value : (prev[id]?.content_en ?? original.content_en),
          content_fr: field === "content_fr" ? value : (prev[id]?.content_fr ?? original.content_fr),
        },
      };
      
      // If both values match original, remove this entry
      if (newChanges[id].content_en === original.content_en && 
          newChanges[id].content_fr === original.content_fr) {
        const { [id]: _, ...rest } = newChanges;
        return rest;
      }
      
      return newChanges;
    });
  };

  const handleToggleVisibility = (group: { title: string; sections: string[] }) => {
    // Get the first section item to determine current visibility
    const firstItem = contentByKey[group.sections[0]];
    if (!firstItem) return;
    
    const newHiddenState = !firstItem.is_hidden;
    
    // Toggle visibility for all sections in the group
    group.sections.forEach(sectionKey => {
      const item = contentByKey[sectionKey];
      if (item) {
        toggleVisibilityMutation.mutate({ id: item.id, is_hidden: newHiddenState });
      }
    });
  };

  const isGroupHidden = (sections: string[]) => {
    const firstItem = contentByKey[sections[0]];
    return firstItem?.is_hidden ?? false;
  };

  const handleSave = (item: PageContent) => {
    const changes = localChanges[item.id];
    updateMutation.mutate({
      id: item.id,
      content_en: changes?.content_en ?? item.content_en,
      content_fr: changes?.content_fr ?? item.content_fr,
    });
  };

  const hasChanges = (id: string) => !!localChanges[id];
  const hasGroupChanges = (sectionKeys: string[]) => {
    if (!pageContent) return false;
    return pageContent.some(item => sectionKeys.includes(item.section_key) && hasChanges(item.id));
  };

  const saveGroupChanges = (sectionKeys: string[]) => {
    if (!pageContent) return;
    const itemsToSave = pageContent.filter(item => sectionKeys.includes(item.section_key) && hasChanges(item.id));
    itemsToSave.forEach(item => handleSave(item));
  };

  const handleAddCategory = () => {
    // Generate a unique prefix
    const existingPrefixes = serviceCategories;
    let num = existingPrefixes.length + 1;
    let newPrefix = `service${num}`;
    while (existingPrefixes.includes(newPrefix)) {
      num++;
      newPrefix = `service${num}`;
    }
    addCategoryMutation.mutate(newPrefix);
  };

  const handleDeleteCategory = (prefix: string) => {
    deleteCategoryMutation.mutate(prefix);
  };

  const [isTranslating, setIsTranslating] = useState(false);

  const handleTranslate = async () => {
    setIsTranslating(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast({
          title: "Error",
          description: "You must be logged in to translate content.",
          variant: "destructive",
        });
        return;
      }

      const response = await supabase.functions.invoke("translate-content", {
        body: { page_slug: selectedPage },
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      queryClient.invalidateQueries({ queryKey: ["page-content"] });
      toast({
        title: "Translation Complete",
        description: response.data.message || "French translations have been updated.",
      });
    } catch (error) {
      console.error("Translation error:", error);
      toast({
        title: "Translation Failed",
        description: error instanceof Error ? error.message : "Failed to translate content.",
        variant: "destructive",
      });
    } finally {
      setIsTranslating(false);
    }
  };

  const currentField = showFrench ? "content_fr" : "content_en";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-display font-bold">Pages</h1>
          <p className="text-muted-foreground mt-1">Manage content for Home, About, Services, and Join pages</p>
        </div>
        <div className="flex items-center gap-4">
          <Button
            onClick={handleTranslate}
            disabled={isTranslating}
            variant="outline"
            size="sm"
          >
            {isTranslating ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Languages className="h-4 w-4 mr-2" />
            )}
            Translate to French
          </Button>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowFrench(false)}
              className={`text-xs uppercase tracking-wide font-medium transition-colors px-2 py-1 border-b-2 ${
                !showFrench 
                  ? "text-primary border-primary" 
                  : "text-muted-foreground hover:text-primary border-transparent"
              }`}
            >
              EN
            </button>
            <span className="text-muted-foreground/50">|</span>
            <button
              onClick={() => setShowFrench(true)}
              className={`text-xs uppercase tracking-wide font-medium transition-colors px-2 py-1 border-b-2 ${
                showFrench 
                  ? "text-primary border-primary" 
                  : "text-muted-foreground hover:text-primary border-transparent"
              }`}
            >
              FR
            </button>
          </div>
        </div>
      </div>

      <Tabs value={selectedPage} onValueChange={setSelectedPage}>
        <div className="flex items-center gap-4">
          <TabsList>
            <TabsTrigger value="home">Home</TabsTrigger>
            <TabsTrigger value="about">About</TabsTrigger>
            <TabsTrigger value="services">Services</TabsTrigger>
            <TabsTrigger value="hours-contact">Hours & Contact</TabsTrigger>
            <TabsTrigger value="for-talent">Join</TabsTrigger>
          </TabsList>
          {hasUnsavedChanges && (
            <div className="flex items-center gap-2 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 px-3 py-1.5 rounded-full text-sm font-medium">
              <AlertTriangle className="h-4 w-4" />
              <span>Unsaved changes</span>
            </div>
          )}
        </div>

        <TabsContent value={selectedPage} className="mt-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-6">
              {sectionGroups.map((group) => {
                const groupItems = group.sections
                  .map(key => contentByKey[key])
                  .filter(Boolean);
                
                if (groupItems.length === 0) return null;

                const groupHidden = isGroupHidden(group.sections);
                const isServiceCategory = selectedPage === "services" && 'prefix' in group && group.prefix;

                return (
                  <Card key={group.title + (isServiceCategory ? group.prefix : '')} className={groupHidden ? "opacity-60" : ""}>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <CardTitle className="text-lg">{group.title}</CardTitle>
                          {groupHidden && (
                            <span className="text-xs bg-muted px-2 py-0.5 rounded text-muted-foreground">Hidden</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {isServiceCategory && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-destructive hover:text-destructive"
                                  title="Delete category"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Service Category?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will permanently delete this service category and all its content. This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDeleteCategory((group as { prefix: string }).prefix)}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                          <Button
                            onClick={() => handleToggleVisibility(group)}
                            disabled={toggleVisibilityMutation.isPending}
                            size="sm"
                            variant="ghost"
                            title={groupHidden ? "Show section" : "Hide section"}
                          >
                            {groupHidden ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            onClick={() => saveGroupChanges(group.sections)}
                            disabled={!hasGroupChanges(group.sections) || updateMutation.isPending}
                            size="sm"
                            variant={hasGroupChanges(group.sections) ? "default" : "outline"}
                          >
                            {updateMutation.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                              <Save className="h-4 w-4 mr-2" />
                            )}
                            Save
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Special 2-column layout for What We Do */}
                      {group.title === "What We Do" ? (
                        <>
                          {/* Title field first */}
                          {contentByKey["whatwedo_title"] && (
                            <div className="space-y-1.5">
                              <label className="text-sm font-medium text-muted-foreground">
                                {SECTION_LABELS["whatwedo_title"]}
                              </label>
                              <Input
                                value={(localChanges[contentByKey["whatwedo_title"].id] ?? contentByKey["whatwedo_title"])[currentField]}
                                onChange={(e) => handleChange(contentByKey["whatwedo_title"].id, currentField, e.target.value, contentByKey["whatwedo_title"])}
                                placeholder={`Enter ${showFrench ? "French" : "English"} content...`}
                              />
                            </div>
                          )}
                          {/* 2-column grid for subtitle/description pairs */}
                          <div className="grid md:grid-cols-[2fr_3fr] gap-x-6 gap-y-4">
                            {[1, 2, 3].map((num) => {
                              const titleKey = num === 1 ? "whatwedo_talent_title" : 
                                               num === 2 ? "whatwedo_casting_title" : "whatwedo_session_title";
                              const descKey = num === 1 ? "whatwedo_talent_desc" : 
                                              num === 2 ? "whatwedo_casting_desc" : "whatwedo_session_desc";
                              const titleItem = contentByKey[titleKey];
                              const descItem = contentByKey[descKey];
                              
                              if (!titleItem || !descItem) return null;
                              
                              const titleValues = localChanges[titleItem.id] ?? { content_en: titleItem.content_en, content_fr: titleItem.content_fr };
                              const descValues = localChanges[descItem.id] ?? { content_en: descItem.content_en, content_fr: descItem.content_fr };
                              const isRichText = RICH_TEXT_FIELDS.includes(descItem.section_key);
                              
                              return (
                                <React.Fragment key={num}>
                                  <div className="space-y-1.5">
                                    <label className="text-sm font-medium text-muted-foreground">
                                      Subtitle {num}
                                    </label>
                                    <Input
                                      value={showFrench ? titleValues.content_fr : titleValues.content_en}
                                      onChange={(e) => handleChange(titleItem.id, currentField, e.target.value, titleItem)}
                                      placeholder={`Enter ${showFrench ? "French" : "English"} content...`}
                                    />
                                  </div>
                                  <div className="space-y-1.5">
                                    <label className="text-sm font-medium text-muted-foreground">
                                      Description {num}
                                    </label>
                                    {isRichText ? (
                                      <RichTextEditor
                                        value={showFrench ? descValues.content_fr : descValues.content_en}
                                        onChange={(value) => handleChange(descItem.id, currentField, value, descItem)}
                                        placeholder={`Enter ${showFrench ? "French" : "English"} content...`}
                                      />
                                    ) : (
                                      <Input
                                        value={showFrench ? descValues.content_fr : descValues.content_en}
                                        onChange={(e) => handleChange(descItem.id, currentField, e.target.value, descItem)}
                                        placeholder={`Enter ${showFrench ? "French" : "English"} content...`}
                                      />
                                    )}
                                  </div>
                                </React.Fragment>
                              );
                            })}
                          </div>
                        </>
                      ) : group.title === "Why Producers Choose Us" ? (
                        <>
                          {/* Title field first */}
                          {contentByKey["whyproducers_title"] && (
                            <div className="space-y-1.5">
                              <label className="text-sm font-medium text-muted-foreground">
                                {SECTION_LABELS["whyproducers_title"]}
                              </label>
                              <Input
                                value={(localChanges[contentByKey["whyproducers_title"].id] ?? contentByKey["whyproducers_title"])[currentField]}
                                onChange={(e) => handleChange(contentByKey["whyproducers_title"].id, currentField, e.target.value, contentByKey["whyproducers_title"])}
                                placeholder={`Enter ${showFrench ? "French" : "English"} content...`}
                              />
                            </div>
                          )}
                          {/* 2-column grid for subtitle/description pairs */}
                          <div className="grid md:grid-cols-[2fr_3fr] gap-x-6 gap-y-4">
                            {[1, 2, 3, 4].map((num) => {
                              const titleKey = num === 1 ? "whyproducers_fast_title" : 
                                               num === 2 ? "whyproducers_bilingual_title" : 
                                               num === 3 ? "whyproducers_pricing_title" : "whyproducers_local_title";
                              const descKey = num === 1 ? "whyproducers_fast_desc" : 
                                              num === 2 ? "whyproducers_bilingual_desc" : 
                                              num === 3 ? "whyproducers_pricing_desc" : "whyproducers_local_desc";
                              const titleItem = contentByKey[titleKey];
                              const descItem = contentByKey[descKey];
                              
                              if (!titleItem || !descItem) return null;
                              
                              const titleValues = localChanges[titleItem.id] ?? { content_en: titleItem.content_en, content_fr: titleItem.content_fr };
                              const descValues = localChanges[descItem.id] ?? { content_en: descItem.content_en, content_fr: descItem.content_fr };
                              
                              return (
                                <React.Fragment key={num}>
                                  <div className="space-y-1.5">
                                    <label className="text-sm font-medium text-muted-foreground">
                                      Subtitle {num}
                                    </label>
                                    <Input
                                      value={showFrench ? titleValues.content_fr : titleValues.content_en}
                                      onChange={(e) => handleChange(titleItem.id, currentField, e.target.value, titleItem)}
                                      placeholder={`Enter ${showFrench ? "French" : "English"} content...`}
                                    />
                                  </div>
                                  <div className="space-y-1.5">
                                    <label className="text-sm font-medium text-muted-foreground">
                                      Description {num}
                                    </label>
                                    <Input
                                      value={showFrench ? descValues.content_fr : descValues.content_en}
                                      onChange={(e) => handleChange(descItem.id, currentField, e.target.value, descItem)}
                                      placeholder={`Enter ${showFrench ? "French" : "English"} content...`}
                                    />
                                  </div>
                                </React.Fragment>
                              );
                            })}
                          </div>
                        </>
                      ) : selectedPage === "services" && group.sections.length === 3 && group.sections[0].endsWith("_title") && group.sections[1].endsWith("_description") && group.sections[2].endsWith("_usecases") ? (
                        /* Services page layout for title/description/usecases */
                        <>
                          {/* Icon and Title row */}
                          {(() => {
                            const titleItem = contentByKey[group.sections[0]];
                            if (!titleItem) return null;
                            const titleValues = localChanges[titleItem.id] ?? { content_en: titleItem.content_en, content_fr: titleItem.content_fr };
                            
                            // Get the prefix from the title section key (e.g., "voiceover" from "voiceover_title")
                            const prefix = group.sections[0].replace("_title", "");
                            const iconItem = contentByKey[`${prefix}_icon`];
                            const iconValue = iconItem?.content_en || "Briefcase";
                            
                            return (
                              <div className="grid md:grid-cols-[180px_1fr] gap-4 items-end">
                                <div className="space-y-1.5">
                                  <label className="text-sm font-medium text-muted-foreground">
                                    Icon
                                  </label>
                                  <IconSelector
                                    value={iconValue}
                                    onChange={(newIcon) => {
                                      if (iconItem) {
                                        // Update both en and fr to same icon
                                        updateMutation.mutate({
                                          id: iconItem.id,
                                          content_en: newIcon,
                                          content_fr: newIcon,
                                        });
                                      }
                                    }}
                                  />
                                </div>
                                <div className="space-y-1.5">
                                  <label className="text-sm font-medium text-muted-foreground">
                                    {SECTION_LABELS[titleItem.section_key] || titleItem.section_key}
                                  </label>
                                  <Input
                                    value={showFrench ? titleValues.content_fr : titleValues.content_en}
                                    onChange={(e) => handleChange(titleItem.id, currentField, e.target.value, titleItem)}
                                    placeholder={`Enter ${showFrench ? "French" : "English"} content...`}
                                  />
                                </div>
                              </div>
                            );
                          })()}
                          {/* 2-column grid for description/usecases */}
                          <div className="grid md:grid-cols-2 gap-x-6 gap-y-4">
                            {[group.sections[1], group.sections[2]].map(sectionKey => {
                              const item = contentByKey[sectionKey];
                              if (!item) return null;

                              const isRichText = RICH_TEXT_FIELDS.includes(item.section_key);
                              const isTextarea = TEXTAREA_FIELDS.includes(item.section_key);
                              const currentValues = localChanges[item.id] ?? { content_en: item.content_en, content_fr: item.content_fr };
                              const displayValue = showFrench ? currentValues.content_fr : currentValues.content_en;

                              return (
                                <div key={item.id} className="space-y-1.5">
                                  <label className="text-sm font-medium text-muted-foreground">
                                    {SECTION_LABELS[item.section_key] || item.section_key}
                                  </label>
                                  {isRichText ? (
                                    <RichTextEditor
                                      value={displayValue}
                                      onChange={(value) => handleChange(item.id, currentField, value, item)}
                                      placeholder={`Enter ${showFrench ? "French" : "English"} content...`}
                                    />
                                  ) : isTextarea ? (
                                    <Textarea
                                      value={displayValue}
                                      onChange={(e) => handleChange(item.id, currentField, e.target.value, item)}
                                      placeholder={`Enter ${showFrench ? "French" : "English"} content (one per line)...`}
                                      rows={6}
                                    />
                                  ) : (
                                    <Input
                                      value={displayValue}
                                      onChange={(e) => handleChange(item.id, currentField, e.target.value, item)}
                                      placeholder={`Enter ${showFrench ? "French" : "English"} content...`}
                                    />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </>
                      ) : selectedPage === "services" && group.sections.length === 2 && group.sections[0].endsWith("_title") && group.sections[1].endsWith("_description") ? (
                        /* Services page 2-column layout for title/description pairs */
                        <div className="grid md:grid-cols-[2fr_3fr] gap-x-6 gap-y-4">
                          {group.sections.map(sectionKey => {
                            const item = contentByKey[sectionKey];
                            if (!item) return null;

                            const isRichText = RICH_TEXT_FIELDS.includes(item.section_key);
                            const currentValues = localChanges[item.id] ?? { content_en: item.content_en, content_fr: item.content_fr };
                            const displayValue = showFrench ? currentValues.content_fr : currentValues.content_en;

                            return (
                              <div key={item.id} className="space-y-1.5">
                                <label className="text-sm font-medium text-muted-foreground">
                                  {SECTION_LABELS[item.section_key] || item.section_key}
                                </label>
                                {isRichText ? (
                                  <RichTextEditor
                                    value={displayValue}
                                    onChange={(value) => handleChange(item.id, currentField, value, item)}
                                    placeholder={`Enter ${showFrench ? "French" : "English"} content...`}
                                  />
                                ) : (
                                  <Input
                                    value={displayValue}
                                    onChange={(e) => handleChange(item.id, currentField, e.target.value, item)}
                                    placeholder={`Enter ${showFrench ? "French" : "English"} content...`}
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        /* Default single-column layout */
                        group.sections.map(sectionKey => {
                          const item = contentByKey[sectionKey];
                          if (!item) return null;

                          const isRichText = RICH_TEXT_FIELDS.includes(item.section_key);
                          const isTextarea = TEXTAREA_FIELDS.includes(item.section_key);
                          const currentValues = localChanges[item.id] ?? { content_en: item.content_en, content_fr: item.content_fr };
                          const displayValue = showFrench ? currentValues.content_fr : currentValues.content_en;

                          return (
                            <div key={item.id} className="space-y-1.5">
                              <label className="text-sm font-medium text-muted-foreground">
                                {SECTION_LABELS[item.section_key] || item.section_key}
                              </label>
                              {isRichText ? (
                                <RichTextEditor
                                  value={displayValue}
                                  onChange={(value) => handleChange(item.id, currentField, value, item)}
                                  placeholder={`Enter ${showFrench ? "French" : "English"} content...`}
                                />
                              ) : isTextarea ? (
                                <Textarea
                                  value={displayValue}
                                  onChange={(e) => handleChange(item.id, currentField, e.target.value, item)}
                                  placeholder={`Enter ${showFrench ? "French" : "English"} content (one per line)...`}
                                  rows={6}
                                />
                              ) : (
                                <Input
                                  value={displayValue}
                                  onChange={(e) => handleChange(item.id, currentField, e.target.value, item)}
                                  placeholder={`Enter ${showFrench ? "French" : "English"} content...`}
                                />
                              )}
                            </div>
                          );
                        })
                      )}
                    </CardContent>
                  </Card>
                );
              })}
              
              {/* Draggable Service Categories */}
              {selectedPage === "services" && serviceCategoryGroups.length > 0 && (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={serviceCategoryGroups.map(g => g.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {serviceCategoryGroups.map((group, index) => (
                      <SortableServiceCard
                        key={group.id}
                        group={{ ...group, title: `Service Category ${index + 1}` }}
                        contentByKey={contentByKey}
                        localChanges={localChanges}
                        showFrench={showFrench}
                        currentField={currentField}
                        handleChange={handleChange}
                        handleToggleVisibility={handleToggleVisibility}
                        handleDeleteCategory={handleDeleteCategory}
                        saveGroupChanges={saveGroupChanges}
                        hasGroupChanges={hasGroupChanges}
                        isGroupHidden={isGroupHidden}
                        toggleVisibilityMutation={toggleVisibilityMutation}
                        updateMutation={updateMutation}
                        onCreateIcon={async (prefix, iconName, displayOrder) => {
                          const { error } = await supabase
                            .from("page_content")
                            .insert({
                              page_slug: "services",
                              section_key: `${prefix}_icon`,
                              content_en: iconName,
                              content_fr: iconName,
                              display_order: displayOrder,
                            });
                          if (!error) {
                            queryClient.invalidateQueries({ queryKey: ["page-content"] });
                          }
                        }}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              )}
              
              {/* Add Service Category button */}
              {selectedPage === "services" && (
                <Button
                  onClick={handleAddCategory}
                  disabled={addCategoryMutation.isPending}
                  variant="outline"
                  className="w-full border-dashed"
                >
                  {addCategoryMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Plus className="h-4 w-4 mr-2" />
                  )}
                  Add Service Category
                </Button>
              )}

              {/* Call to Action */}
              {selectedPage === "services" && (() => {
                const ctaGroup = SERVICES_STATIC_GROUPS.cta;
                const groupItems = ctaGroup.sections
                  .map(key => contentByKey[key])
                  .filter(Boolean);
                
                if (groupItems.length === 0) return null;

                const groupHidden = isGroupHidden(ctaGroup.sections);

                return (
                  <Card className={groupHidden ? "opacity-60" : ""}>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <CardTitle className="text-lg">{ctaGroup.title}</CardTitle>
                          {groupHidden && (
                            <span className="text-xs bg-muted px-2 py-0.5 rounded text-muted-foreground">Hidden</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            onClick={() => handleToggleVisibility(ctaGroup)}
                            disabled={toggleVisibilityMutation.isPending}
                            size="sm"
                            variant="ghost"
                            title={groupHidden ? "Show section" : "Hide section"}
                          >
                            {groupHidden ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            onClick={() => saveGroupChanges(ctaGroup.sections)}
                            disabled={!hasGroupChanges(ctaGroup.sections) || updateMutation.isPending}
                            size="sm"
                            variant={hasGroupChanges(ctaGroup.sections) ? "default" : "outline"}
                          >
                            {updateMutation.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                              <Save className="h-4 w-4 mr-2" />
                            )}
                            Save
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {ctaGroup.sections.map(sectionKey => {
                        const item = contentByKey[sectionKey];
                        if (!item) return null;

                        const currentValues = localChanges[item.id] ?? { content_en: item.content_en, content_fr: item.content_fr };
                        const displayValue = showFrench ? currentValues.content_fr : currentValues.content_en;

                        return (
                          <div key={item.id} className="space-y-1.5">
                            <label className="text-sm font-medium text-muted-foreground">
                              {SECTION_LABELS[item.section_key] || item.section_key}
                            </label>
                            <Input
                              value={displayValue}
                              onChange={(e) => handleChange(item.id, currentField, e.target.value, item)}
                              placeholder={`Enter ${showFrench ? "French" : "English"} content...`}
                            />
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Hours of Operation */}
              {selectedPage === "hours-contact" && (() => {
                const hoursGroup = SERVICES_STATIC_GROUPS.hours;
                
                // Time options for open/close dropdowns
                const openTimeOptions = [
                  { value: "Closed", label: showFrench ? "Fermé" : "Closed", fr: "Fermé" },
                  { value: "8 a.m.", label: showFrench ? "8 h" : "8 a.m.", fr: "8 h" },
                  { value: "8:30 a.m.", label: showFrench ? "8 h 30" : "8:30 a.m.", fr: "8 h 30" },
                  { value: "9 a.m.", label: showFrench ? "9 h" : "9 a.m.", fr: "9 h" },
                  { value: "9:30 a.m.", label: showFrench ? "9 h 30" : "9:30 a.m.", fr: "9 h 30" },
                  { value: "10 a.m.", label: showFrench ? "10 h" : "10 a.m.", fr: "10 h" },
                  { value: "10:30 a.m.", label: showFrench ? "10 h 30" : "10:30 a.m.", fr: "10 h 30" },
                  { value: "11 a.m.", label: showFrench ? "11 h" : "11 a.m.", fr: "11 h" },
                  { value: "11:30 a.m.", label: showFrench ? "11 h 30" : "11:30 a.m.", fr: "11 h 30" },
                  { value: "12 p.m.", label: showFrench ? "12 h" : "12 p.m.", fr: "12 h" },
                  { value: "12:30 p.m.", label: showFrench ? "12 h 30" : "12:30 p.m.", fr: "12 h 30" },
                  { value: "1 p.m.", label: showFrench ? "13 h" : "1 p.m.", fr: "13 h" },
                  { value: "1:30 p.m.", label: showFrench ? "13 h 30" : "1:30 p.m.", fr: "13 h 30" },
                  { value: "2 p.m.", label: showFrench ? "14 h" : "2 p.m.", fr: "14 h" },
                ];

                const closeTimeOptions = [
                  { value: "4 p.m.", label: showFrench ? "16 h" : "4 p.m.", fr: "16 h" },
                  { value: "4:30 p.m.", label: showFrench ? "16 h 30" : "4:30 p.m.", fr: "16 h 30" },
                  { value: "5 p.m.", label: showFrench ? "17 h" : "5 p.m.", fr: "17 h" },
                  { value: "5:30 p.m.", label: showFrench ? "17 h 30" : "5:30 p.m.", fr: "17 h 30" },
                  { value: "6 p.m.", label: showFrench ? "18 h" : "6 p.m.", fr: "18 h" },
                  { value: "6:30 p.m.", label: showFrench ? "18 h 30" : "6:30 p.m.", fr: "18 h 30" },
                  { value: "7 p.m.", label: showFrench ? "19 h" : "7 p.m.", fr: "19 h" },
                  { value: "7:30 p.m.", label: showFrench ? "19 h 30" : "7:30 p.m.", fr: "19 h 30" },
                  { value: "8 p.m.", label: showFrench ? "20 h" : "8 p.m.", fr: "20 h" },
                ];

                // Get all section keys for save button
                const allHoursSections = [
                  ...hoursGroup.sections,
                  ...hoursGroup.days.flatMap(d => [d.open, d.close])
                ];

                return (
                  <Card>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">{hoursGroup.title}</CardTitle>
                        <Button
                          onClick={() => saveGroupChanges(allHoursSections)}
                          disabled={!hasGroupChanges(allHoursSections) || updateMutation.isPending}
                          size="sm"
                          variant={hasGroupChanges(allHoursSections) ? "default" : "outline"}
                        >
                          {updateMutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          ) : (
                            <Save className="h-4 w-4 mr-2" />
                          )}
                          Save
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Section title field */}
                      {hoursGroup.sections.filter(s => s === "hours_title").map(sectionKey => {
                        const item = contentByKey[sectionKey];
                        if (!item) return null;
                        const currentValues = localChanges[item.id] ?? { content_en: item.content_en, content_fr: item.content_fr };
                        const displayValue = showFrench ? currentValues.content_fr : currentValues.content_en;
                        return (
                          <div key={item.id} className="space-y-1.5">
                            <label className="text-sm font-medium text-muted-foreground">
                              {SECTION_LABELS[item.section_key] || "Section Title"}
                            </label>
                            <Input
                              value={displayValue}
                              onChange={(e) => handleChange(item.id, currentField, e.target.value, item)}
                              placeholder={`Enter ${showFrench ? "French" : "English"} content...`}
                            />
                          </div>
                        );
                      })}

                      {/* Days with open/close dropdowns */}
                      <div className="space-y-3">
                        {hoursGroup.days.map(({ day, open, close }) => {
                          const openItem = contentByKey[open];
                          const closeItem = contentByKey[close];
                          if (!openItem) return null;

                          const openValues = localChanges[openItem.id] ?? { content_en: openItem.content_en, content_fr: openItem.content_fr };
                          const isClosed = openValues.content_en === "Closed";

                          return (
                            <div key={day} className="grid grid-cols-[100px_1fr_1fr] gap-3 items-center">
                              <span className="text-sm font-medium">{day}</span>
                              <Select
                                value={openValues.content_en}
                                onValueChange={(value) => {
                                  const option = openTimeOptions.find(o => o.value === value);
                                  handleChange(openItem.id, "content_en", value, openItem);
                                  handleChange(openItem.id, "content_fr", option?.fr || value, openItem);
                                }}
                              >
                                <SelectTrigger className="bg-background">
                                  <SelectValue placeholder="Open..." />
                                </SelectTrigger>
                                <SelectContent className="bg-background z-50">
                                  {openTimeOptions.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {closeItem && !isClosed && (
                                <Select
                                  value={(localChanges[closeItem.id] ?? { content_en: closeItem.content_en }).content_en}
                                  onValueChange={(value) => {
                                    const option = closeTimeOptions.find(o => o.value === value);
                                    handleChange(closeItem.id, "content_en", value, closeItem);
                                    handleChange(closeItem.id, "content_fr", option?.fr || value, closeItem);
                                  }}
                                >
                                  <SelectTrigger className="bg-background">
                                    <SelectValue placeholder="Close..." />
                                  </SelectTrigger>
                                  <SelectContent className="bg-background z-50">
                                    {closeTimeOptions.map((option) => (
                                      <SelectItem key={option.value} value={option.value}>
                                        {option.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                              {isClosed && <div className="text-muted-foreground text-sm italic">—</div>}
                            </div>
                          );
                        })}
                      </div>

                      {/* Weekend note field */}
                      {hoursGroup.sections.filter(s => s === "hours_weekend").map(sectionKey => {
                        const item = contentByKey[sectionKey];
                        if (!item) return null;
                        const currentValues = localChanges[item.id] ?? { content_en: item.content_en, content_fr: item.content_fr };
                        const displayValue = showFrench ? currentValues.content_fr : currentValues.content_en;
                        return (
                          <div key={item.id} className="space-y-1.5 pt-2">
                            <label className="text-sm font-medium text-muted-foreground">
                              {SECTION_LABELS[item.section_key] || "Weekend Note"}
                            </label>
                            <Input
                              value={displayValue}
                              onChange={(e) => handleChange(item.id, currentField, e.target.value, item)}
                              placeholder={`Enter ${showFrench ? "French" : "English"} content...`}
                            />
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Contact Information */}
              {selectedPage === "hours-contact" && (() => {
                const contactGroup = SERVICES_STATIC_GROUPS.contact;
                const groupItems = contactGroup.sections
                  .map(key => contentByKey[key])
                  .filter(Boolean);
                
                if (groupItems.length === 0) return null;

                return (
                  <Card>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">{contactGroup.title}</CardTitle>
                        <Button
                          onClick={() => saveGroupChanges(contactGroup.sections)}
                          disabled={!hasGroupChanges(contactGroup.sections) || updateMutation.isPending}
                          size="sm"
                          variant={hasGroupChanges(contactGroup.sections) ? "default" : "outline"}
                        >
                          {updateMutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          ) : (
                            <Save className="h-4 w-4 mr-2" />
                          )}
                          Save
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-3 gap-4">
                        {contactGroup.sections.map(sectionKey => {
                          const item = contentByKey[sectionKey];
                          if (!item) return null;

                          const currentValues = localChanges[item.id] ?? { content_en: item.content_en, content_fr: item.content_fr };
                          const displayValue = showFrench ? currentValues.content_fr : currentValues.content_en;

                          return (
                            <div key={item.id} className="space-y-1.5">
                              <label className="text-sm font-medium text-muted-foreground">
                                {SECTION_LABELS[item.section_key] || item.section_key}
                              </label>
                              <Input
                                value={displayValue}
                                onChange={(e) => handleChange(item.id, currentField, e.target.value, item)}
                                placeholder={`Enter ${showFrench ? "French" : "English"} content...`}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                );
              })()}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PagesAdmin;
